/**
 * ScopePicker — header pill: "Viewing as: [Self / Team / Org]".
 *
 * Options visible to the user depend on their permissions:
 *   - Everyone: Self
 *   - Department heads: + Team
 *   - Owners/admins: + Org
 *
 * Changing scope:
 *   1. Updates localStorage (persists across reloads)
 *   2. Fires `waypoint:scope-changed` window event
 *   3. We invalidate the relevant React Query keys so widgets refetch
 *      with the new X-Waypoint-Scope header (api() reads it on each call)
 */
import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useMe } from '@/lib/hooks/use-me';
import { useViewScope, type ScopeLevel } from '@/lib/hooks/view-scope';

const LABEL: Record<ScopeLevel, string> = {
  self: 'Just you',
  team: 'Your team',
  org:  'Org-wide',
};

const DESCRIPTION: Record<ScopeLevel, string> = {
  self: 'Only your attributed data',
  team: 'You + every member of departments you head',
  org:  'Everything across the workspace',
};

const ICON: Record<ScopeLevel, JSX.Element> = {
  self: (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a6 6 0 016-6h4a6 6 0 016 6v2" /></svg>
  ),
  team: (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
  ),
  org: (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18M3 9h18" /></svg>
  ),
};

export function ScopePicker() {
  const { scope, setScope } = useViewScope();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Build available options from the effective permissions
  // (role default OR granted_scopes).
  const options: ScopeLevel[] = ['self'];
  if (me.permissions.canViewTeam) options.push('team');
  if (me.permissions.canViewOrg) options.push('org');

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Refetch on scope change lives in ViewScopeProvider (lib/hooks/view-scope.tsx).

  if (options.length === 1) return null; // nothing to pick if user can only see self

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-2xs font-medium text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors"
        title={DESCRIPTION[scope]}
      >
        <span className="text-ink-dim">{ICON[scope]}</span>
        <span className="text-ink-dim hidden sm:inline">Viewing as:</span>
        <span className="font-semibold text-ink">{LABEL[scope]}</span>
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={clsx('transition-transform', open && 'rotate-180')}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-modal mt-1 w-[240px] rounded-lg border border-border bg-bg-2 shadow-lift-lg overflow-hidden animate-fade-in">
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => { setScope(opt); setOpen(false); }}
              className={clsx(
                'flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors',
                opt === scope ? 'bg-primary/10' : 'hover:bg-surface-2',
              )}
            >
              <span className={clsx('mt-0.5 shrink-0', opt === scope ? 'text-primary' : 'text-ink-dim')}>
                {ICON[opt]}
              </span>
              <div className="min-w-0 flex-1">
                <div className={clsx('text-xs font-semibold', opt === scope ? 'text-primary' : 'text-ink')}>
                  {LABEL[opt]}
                </div>
                <div className="text-2xs text-ink-dim leading-snug">{DESCRIPTION[opt]}</div>
              </div>
              {opt === scope && (
                <svg aria-hidden="true" className="text-primary shrink-0 mt-0.5" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
